import { pickCashflowCoveredLeader, CASHFLOW_COVERED_LABEL, type CashflowCoveredVerdict } from './cashflowCoveredGoal.ts';
import { leadWithinTie, leadExceptionOf, pickSecondPlan, type LeadException, type PlanReadings, type SecondPlan } from './planChoiceRule.ts';
/**
 * Pick goal-leader strategies for Support at Home (mirrors Aged Care suggestion engine buckets).
 */

/**
 * Snapshot metrics attached to a server-evaluated strategy result (evaluateSupportAtHomeStrategies,
 * evaluateGrannyFlatCandidates, evaluateRetirementVillageCandidates, evaluateAgedCareRadFundingScenarios).
 * Field names vary slightly by engine (legacy `cashflow`/`cashFlow` vs newer `netCashflow`,
 * `pension`/`agePension` absolute vs `agePensionIncrease` marginal) — all kept optional and read
 * defensively via `??` chains, matching how the calc engine actually populates them per suggestion module.
 */
export interface StrategySnapshot {
    netCashflow?: number;
    cashflow?: number;
    cashFlow?: number;
    cashflowImprovement?: number;
    pension?: number;
    agePension?: number;
    agePensionIncrease?: number;
    legacyValue?: number;
    legacyImprovement?: number;
    contributionReduction?: number;
    contributionRates?: { clinical?: number; independence?: number; everydayLiving?: number };
    [key: string]: unknown;
}

/** A candidate strategy config as evaluated (and returned) by the server-side suggestion engine. */
export interface EvaluatedStrategy {
    name?: string;
    snapshot?: StrategySnapshot;
    config?: { type?: string; [key: string]: unknown };
    score?: number;
    recommendationLabels?: string[];
    [key: string]: unknown;
}

export interface RecommendedStrategyEntry {
    label: string;
    strategy: EvaluatedStrategy;
    /** For the cashflow-covered goal: which of its three cases chose this leader (cashflowCoveredGoal.ts). */
    cashflowCovered?: CashflowCoveredVerdict;
    /** For the second card (planChoiceRule.ts): why it earns its place beside the estate leader. */
    second?: SecondPlan<EvaluatedStrategy>;
    /** For the estate leader: set when it is NOT the plan that leaves the most (planChoiceRule.ts rule 2). */
    lead?: LeadException;
}

/** The label of the second card beside the estate leader, when the plan-choice rule lets one through. */
export const NEAR_BEST_LABEL = 'Near-best alternative';

const DO_NOTHING_NAME = 'Do Nothing';

function isBaselineStrategy(strategy: EvaluatedStrategy | null | undefined): boolean {
    return strategy?.name === DO_NOTHING_NAME || strategy?.config?.type === 'doNothing';
}

function getNetCashflow(strategy: EvaluatedStrategy | null | undefined): number {
    const s = strategy?.snapshot;
    return s?.netCashflow ?? s?.cashflow ?? s?.cashFlow ?? -Infinity;
}

function getPension(strategy: EvaluatedStrategy | null | undefined): number {
    const s = strategy?.snapshot;
    return s?.pension ?? s?.agePension ?? 0;
}

function getLegacyValue(strategy: EvaluatedStrategy | null | undefined): number {
    return strategy?.snapshot?.legacyValue ?? -Infinity;
}

export type StrategyGoalDimension = 'legacy' | 'cashflow' | 'pension' | 'covered';

/**
 * True when strategy strictly improves on the given goal vs Do Nothing baseline.
 */
export function strategyBeatsDoNothing(
    strategy: EvaluatedStrategy | null | undefined,
    doNothingStrategy: EvaluatedStrategy | null | undefined,
    dimension: StrategyGoalDimension,
): boolean {
    if (!doNothingStrategy?.snapshot || isBaselineStrategy(strategy)) return false;
    const s = strategy?.snapshot || {};

    if (dimension === 'legacy') {
        if (typeof s.legacyImprovement === 'number') return s.legacyImprovement > 0;
        return getLegacyValue(strategy) > getLegacyValue(doNothingStrategy);
    }
    if (dimension === 'cashflow') {
        if (typeof s.cashflowImprovement === 'number') return s.cashflowImprovement > 0;
        return getNetCashflow(strategy) > getNetCashflow(doNothingStrategy);
    }
    if (dimension === 'pension') {
        // Goal leader = highest modelled Age Pension (absolute), not marginal increase only.
        return getPension(strategy) > getPension(doNothingStrategy);
    }
    return true;
}

/**
 * @param strategies - Evaluated strategies (excluding baseline is OK; baseline is skipped)
 * @param doNothingStrategy - Baseline for never-worse-than-do-nothing gate on goal cards
 */
export interface RecommendedOptionsInput {
    /** The yearly surplus "cashflow covered" must keep. Zero means the bills are paid. */
    cashflowTarget?: number;
    /**
     * The module's readings of a plan (planChoiceRule.ts). Given: a near-tie on the estate goals is led
     * by the plan that asks less, and a second card is offered only for a real trade-off.
     */
    readings?: PlanReadings<EvaluatedStrategy>;
}

export function buildRecommendedOptimalStrategies(
    strategies: EvaluatedStrategy[] | null | undefined,
    doNothingStrategy: EvaluatedStrategy | null = null,
    options: RecommendedOptionsInput = {},
): RecommendedStrategyEntry[] {
    const universe = (strategies || []).filter((s) => !isBaselineStrategy(s));
    if (universe.length === 0) return [];

    const legacyPool = doNothingStrategy
        ? universe.filter((s) => strategyBeatsDoNothing(s, doNothingStrategy, 'legacy'))
        : universe;
    const cashflowPool = doNothingStrategy
        ? universe.filter((s) => strategyBeatsDoNothing(s, doNothingStrategy, 'cashflow'))
        : universe;
    const pensionPool = doNothingStrategy
        ? universe.filter((s) => strategyBeatsDoNothing(s, doNothingStrategy, 'pension'))
        : universe;

    const byLegacy = [...(doNothingStrategy ? legacyPool : universe)].sort(
        (a, b) => getLegacyValue(b) - getLegacyValue(a)
    );
    // A near-tie is a tie: the plan that asks less leads it (planChoiceRule.ts, owner 20 Oct 2026).
    const bestLegacy = options.readings ? leadWithinTie(byLegacy, options.readings) : byLegacy[0];
    const cashflowUniverse = doNothingStrategy ? cashflowPool : universe;
    const bestCashflow = cashflowUniverse.length
        ? cashflowUniverse.reduce(
            // A tie (within a dollar) goes to the plan that leaves more (owner's file, 22 Oct 2026).
            (best, s) => (getNetCashflow(s) > getNetCashflow(best) + 1 || (Math.abs(getNetCashflow(s) - getNetCashflow(best)) < 1 && getLegacyValue(s) > getLegacyValue(best) + 1) ? s : best),
            cashflowUniverse[0]
        )
        : null;
    const pensionUniverse = doNothingStrategy ? pensionPool : universe;
    const bestPension = pensionUniverse.length
        ? pensionUniverse.reduce(
            // A tie (within a dollar) goes to the plan that leaves more: the same pension with the credit card also
            // paid off and more left to the family was beaten by list order (owner's file, 22 Oct 2026).
            (best, s) => (getPension(s) > getPension(best) + 1 || (Math.abs(getPension(s) - getPension(best)) < 1 && getLegacyValue(s) > getLegacyValue(best) + 1) ? s : best),
            pensionUniverse[0]
        )
        : null;

    const recommended: RecommendedStrategyEntry[] = [];

    const add = (strategy: EvaluatedStrategy | null | undefined, label: string, requireBeat = true): boolean => {
        if (!strategy?.name) return false;
        if (recommended.some((r) => r.label === label)) return false;
        if (requireBeat && doNothingStrategy) {
            const dim: StrategyGoalDimension = label.includes('Cashflow') ? 'cashflow'
                : label.includes('Pension') ? 'pension'
                    : 'legacy';
            if (!strategyBeatsDoNothing(strategy, doNothingStrategy, dim)) return false;
        }
        recommended.push({ label, strategy });
        return true;
    };

    /*
     * THE FOURTH GOAL, FIRST (owner, 24 Sep 2026): the best estate among the plans that still pay
     * the bills — or, failing that, whose shortfall the savings carry for the whole period — or,
     * failing that, the one that lasts longest, badged as such. It is the default chip in every
     * module, so it is ranked before the others and never has to beat do-nothing to exist: a client
     * who has decided to move still needs to know which plan carries them, even if none beats
     * staying put on the estate.
     */
    const covered = pickCashflowCoveredLeader(universe, { cashflowTarget: options.cashflowTarget, readings: options.readings });
    if (covered?.strategy?.name) recommended.push({ label: CASHFLOW_COVERED_LABEL, strategy: covered.strategy, cashflowCovered: covered.verdict });

    if (bestLegacy && add(bestLegacy, 'Optimal Legacy') && options.readings) {
        const ex = leadExceptionOf(byLegacy, bestLegacy, options.readings);
        if (ex) recommended[recommended.length - 1]!.lead = ex;
    }
    if (bestCashflow) add(bestCashflow, 'Optimal Cashflow');

    /*
     * The Age Pension goal has a leader only when a plan pays one. The next-best estate used to be put
     * under it when none did — a runner-up badged as the pension's best (it never passed the gate, but
     * aged care's copy did). With no pension, the goal says so instead (owner, 20 Oct 2026).
     */
    if (bestPension && getPension(bestPension) > 0) add(bestPension, 'Optimal Age Pension');

    // A second card beside the estate leader, only for a real trade-off (planChoiceRule.ts).
    if (options.readings && bestLegacy) {
        const second = pickSecondPlan(byLegacy, bestLegacy, options.readings, (a, b) => a.name === b.name);
        if (second?.strategy?.name) recommended.push({ label: NEAR_BEST_LABEL, strategy: second.strategy, second });
    }

    return recommended;
}

export function enrichStrategiesWithRecommendationLabels(
    strategies: EvaluatedStrategy[] | null | undefined,
    recommendedOptimalStrategies: RecommendedStrategyEntry[] | null | undefined,
): EvaluatedStrategy[] {
    const labelsByName = new Map<string, string[]>();
    (recommendedOptimalStrategies || []).forEach(({ label, strategy }) => {
        if (!strategy?.name || !label) return;
        const existing = labelsByName.get(strategy.name) || [];
        if (!existing.includes(label)) existing.push(label);
        labelsByName.set(strategy.name, existing);
    });

    return (strategies || []).map((s) => ({
        ...s,
        recommendationLabels: (s.name && labelsByName.get(s.name)?.length)
            ? labelsByName.get(s.name)
            : s.recommendationLabels,
    }));
}

/**
 * The recommended entry for one optimisation surface, or null when no strategy beat Do Nothing on
 * that goal. Null means the UI must SAY so — never substitute another goal's leader. The
 * substitution showed one client the same "Optimal Cashflow" card under Best legacy and Best
 * pension, beneath a banner claiming it was the best-estate pick (adviser-reported, 2026-08-19).
 */
export function goalLeaderFor(
    recommendedOptimalStrategies: RecommendedStrategyEntry[] | null | undefined,
    surface: StrategyGoalDimension,
): RecommendedStrategyEntry | null {
    const label = surface === 'cashflow' ? 'Optimal Cashflow'
        : surface === 'pension' ? 'Optimal Age Pension'
            : surface === 'covered' ? CASHFLOW_COVERED_LABEL
                : 'Optimal Legacy';
    return (recommendedOptimalStrategies || []).find((r) => r.label === label && r.strategy?.name) ?? null;
}

export function mergeGoalLeaderStrategies(recommendedOptimalStrategies: RecommendedStrategyEntry[] | null | undefined): EvaluatedStrategy[] {
    const byStrategyName = new Map<string, EvaluatedStrategy>();
    (recommendedOptimalStrategies || []).forEach((row) => {
        const name = row?.strategy?.name;
        if (!name || !row.strategy) return;
        if (!byStrategyName.has(name)) {
            byStrategyName.set(name, { ...row.strategy, recommendationLabels: [row.label] });
            return;
        }
        const existing = byStrategyName.get(name);
        const labels = existing?.recommendationLabels || [];
        if (row.label && !labels.includes(row.label)) labels.push(row.label);
        byStrategyName.set(name, { ...(existing as EvaluatedStrategy), recommendationLabels: labels });
    });
    return Array.from(byStrategyName.values());
}
